/**
 * src/features/sync/ui/SyncStatusBadge.tsx
 *
 * Non-blocking inline header pill showing sync state (CLOUD-05).
 *
 * Design constraints:
 *   - NO modal, NO z-index overlay, NO spinner gating the editor
 *   - Inline pill only — renders null when status is 'idle'
 *   - Subscribes to useSyncStore; no server dependency
 *   - i18n: t('sync.*', 'Fallback') in all 4 locales (ru/uk/en/pl)
 *
 * Positioning: mount in App.tsx header area (near CommandMenu).
 *
 * See RESEARCH §Area 3 and §Pattern 7 for design rationale.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { useSyncStore } from '@/stores/useSyncStore';

/**
 * WR-01 fix: compute elapsed minutes as a pure helper outside the component.
 * Keeping Date.now() here (not in the component body) satisfies react-hooks/purity.
 * Returns minutes elapsed since the given timestamp (0 if < 1 minute).
 */
function elapsedMinutes(timestampMs: number): number {
  return Math.floor((Date.now() - timestampMs) / 60_000);
}

/**
 * SyncStatusBadge — renders an unobtrusive pill in the header.
 * Returns null when status is 'idle' (no applicable label — no empty space).
 */
export function SyncStatusBadge() {
  const status = useSyncStore((s) => s.status);
  const lastSyncedAt = useSyncStore((s) => s.lastSyncedAt);
  const rejectedCount = useSyncStore((s) => s.rejectedCount);
  const { t } = useTranslation();

  // WR-01 fix: compute label via i18n instead of hardcoded English strings.
  // Pass `count` (minutes) to translation so each locale handles pluralization.
  // Sub-minute uses sync.synced_just_now; N-minute uses sync.synced with count.
  const diffMin = lastSyncedAt ? elapsedMinutes(lastSyncedAt) : 0;

  const label =
    status === 'offline'
      ? t('sync.offline', 'Offline')
      : status === 'syncing'
        ? t('sync.syncing', 'Syncing...')
        : status === 'error'
          ? rejectedCount > 0
            ? t('sync.error_rejected', "{{count}} change(s) couldn't be saved", { count: rejectedCount })
            : t('sync.error', 'Sync error')
          : status === 'conflict'
            ? t('sync.conflict', 'Sync conflict — resolved')
            : status === 'synced' && lastSyncedAt
              ? diffMin < 1
                ? t('sync.synced_just_now', 'Synced just now')
                : t('sync.synced', 'Synced {{count}} min ago', { count: diffMin })
              : null;

  // 'idle' or 'synced' without a timestamp → render nothing
  if (!label) return null;

  return (
    <span
      className={cn('text-xs px-2 py-0.5 rounded-full', {
        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': status === 'offline',
        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400': status === 'syncing',
        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': status === 'synced',
        'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400':
          status === 'error' || status === 'conflict',
      })}
    >
      {label}
    </span>
  );
}
